import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { PropertyCard } from "@/components/PropertyCard";
import { SectionTitle } from "@/components/Bits";
import { PROPERTY_TYPES, COUNTRIES } from "@/lib/constants";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SlidersHorizontal } from "lucide-react";

export default function Properties({ transaction }) {
  const { t } = useLang();
  const [params, setParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: params.get("type") || "",
    country: params.get("country") || "",
    city: params.get("city") || "",
    min_price: params.get("min_price") || "",
    max_price: params.get("max_price") || "",
  });

  const isRent = transaction === "location";
  const eyebrow = t(isRent ? "properties.rentEyebrow" : "properties.buyEyebrow");
  const title = t(isRent ? "properties.rentTitle" : "properties.buyTitle");
  const subtitle = t(isRent ? "properties.rentSubtitle" : "properties.buySubtitle");

  const load = async () => {
    setLoading(true);
    try {
      const query = { transaction, limit: 60 };
      Object.entries(filters).forEach(([k, v]) => { if (v) query[k] = v; });
      const { data } = await api.get("/properties", { params: query });
      setProperties(data);
    } finally { setLoading(false); }
  };
  const loadFavs = async () => { try { const { data } = await api.get("/favorites"); setFavIds(data.map((p) => p.id)); } catch {} };

  useEffect(() => { load(); loadFavs(); /* eslint-disable-next-line */ }, [transaction]);
  const set = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const apply = () => { const np = {}; Object.entries(filters).forEach(([k, v]) => { if (v) np[k] = v; }); setParams(np); load(); };
  const reset = () => { setFilters({ type: "", country: "", city: "", min_price: "", max_price: "" }); setParams({}); setTimeout(load, 0); };

  return (
    <div className="bg-ivory min-h-screen">
      <section className="bg-navy py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <SectionTitle light eyebrow={eyebrow} title={title} subtitle={subtitle} />
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 py-10">
        <div className="rounded-xl bg-white border border-black/5 p-5 mb-8" data-testid="filters">
          <div className="flex items-center gap-2 mb-4 text-navy font-semibold"><SlidersHorizontal className="h-4 w-4 text-gold" /> {t("properties.filterTitle")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
            <Select value={filters.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="filter-type"><SelectValue placeholder={t("properties.type")} /></SelectTrigger>
              <SelectContent>{PROPERTY_TYPES.map((tp) => <SelectItem key={tp} value={tp}>{tp}</SelectItem>)}</SelectContent>
            </Select>
            <Select value={filters.country} onValueChange={(v) => set("country", v)}>
              <SelectTrigger data-testid="filter-country"><SelectValue placeholder={t("properties.country")} /></SelectTrigger>
              <SelectContent>{COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder={t("properties.city")} value={filters.city} onChange={(e) => set("city", e.target.value)} data-testid="filter-city" />
            <Input type="number" placeholder={t("properties.minBudget")} value={filters.min_price} onChange={(e) => set("min_price", e.target.value)} data-testid="filter-min" />
            <Input type="number" placeholder={t("properties.maxBudget")} value={filters.max_price} onChange={(e) => set("max_price", e.target.value)} data-testid="filter-max" />
          </div>
          <div className="mt-4 flex gap-3">
            <Button onClick={apply} data-testid="apply-filters" className="bg-navy text-white hover:bg-navy-600 rounded-lg">{t("properties.apply")}</Button>
            <Button onClick={reset} variant="outline" data-testid="reset-filters" className="rounded-lg">{t("properties.reset")}</Button>
          </div>
        </div>

        <p className="text-sm text-slate-500 mb-6" data-testid="results-count">{loading ? t("properties.loading") : `${properties.length} ${t("properties.results")}`}</p>

        {loading ? (
          <div className="flex justify-center py-20"><div className="h-10 w-10 animate-spin rounded-full border-4 border-gold border-t-transparent" /></div>
        ) : properties.length === 0 ? (
          <div className="text-center py-20 text-slate-500" data-testid="no-results">{t("properties.none")}</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {properties.map((p) => <PropertyCard key={p.id} property={p} favored={favIds.includes(p.id)} onFavChange={loadFavs} />)}
          </div>
        )}
      </div>
    </div>
  );
}
